import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc
} from 'firebase/firestore';
import { db } from '../../firebase';
import { subscribeLive } from '../../utils/liveData';
import PlanReminders from './PlanReminders';
import './KesifList.css';

const MONTH_NAMES = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const AUTOSAVE_DELAY = 2500;
const LIVE_DAYS = 90; // Son 3 ay canlı yüklenir, daha eskisi istenince getirilir
const OLDER_PAGE_SIZE = 30;
const SEARCH_LIMIT = 30;
const SEARCH_DELAY = 400;

const normalize = (text) => (text || '').toLocaleLowerCase('tr-TR');

// Başlıktaki her kelimenin baş harflerinden oluşan anahtarlar ("ahmet" -> a, ah, ahm, ...).
// Yüklenmemiş eski notlarda başlığa göre sunucuda arama yapabilmek için kaydedilir.
const buildSearchKeywords = (title) => {
  const keywords = new Set();
  normalize(title).split(/[^0-9a-zçğıöşüâîû]+/i).filter(Boolean).forEach(word => {
    for (let i = 1; i <= Math.min(word.length, 20); i++) {
      keywords.add(word.slice(0, i));
    }
  });
  return Array.from(keywords).slice(0, 300);
};

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
};

const formatDate = (date) => {
  if (!date) return '';
  return `${date.getDate()}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
};

// iPhone Notlar gibi: Son 30 Gün, sonra ay ay (farklı yılsa yıl da yazılır)
const getGroupLabel = (date, now) => {
  if (!date) return 'Tarihsiz';
  const diffDays = (now - date) / (1000 * 60 * 60 * 24);
  if (diffDays <= 30) return 'Son 30 Gün';
  const month = MONTH_NAMES[date.getMonth()];
  return date.getFullYear() === now.getFullYear() ? month : `${month} ${date.getFullYear()}`;
};

const getPreview = (content) => {
  const firstLine = (content || '').split('\n').map(l => l.trim()).find(Boolean);
  return firstLine || 'Ek metin yok';
};

const KesifList = ({ user, onlyOwnData = false, pageTitle = 'Keşif & Montaj', reminderTypes, reminderDefaultFilter }) => {
  const [notes, setNotes] = useState([]); // Son 3 ay (canlı)
  const [olderNotes, setOlderNotes] = useState([]); // "Daha eski notlar" ile getirilenler
  const [olderCursor, setOlderCursor] = useState(null);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [searchResults, setSearchResults] = useState([]); // Sunucuda başlıkla bulunan eski notlar
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const cutoffRef = useRef(new Date(Date.now() - LIVE_DAYS * 24 * 60 * 60 * 1000));

  // Editör durumu
  const [editorOpen, setEditorOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const noteIdRef = useRef(null);
  const createPromiseRef = useRef(null);
  const saveTimerRef = useRef(null);
  const latestRef = useRef({ title: '', content: '' });
  const dirtyRef = useRef(false);
  const contentRef = useRef(null);

  // Sadece son 3 ayın notlarını canlı dinle
  useEffect(() => {
    const q = query(
      collection(db, 'kesifNotes'),
      where('updatedAt', '>=', cutoffRef.current),
      orderBy('updatedAt', 'desc')
    );
    // Paylaşılan dinleyici: sekmeye her dönüşte notlar yeniden okunmaz
    const unsubscribe = subscribeLive(
      'kesifNotes:recent',
      q,
      (snapshot) => {
        const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setNotes(docs);
        setLoading(false);
        // Arama anahtarı olmayan eski kayıtları bir kereliğine tamamla
        docs.filter(n => !n.searchKeywords && n.title).forEach(n => {
          updateDoc(doc(db, 'kesifNotes', n.id), { searchKeywords: buildSearchKeywords(n.title) })
            .catch(() => { /* yetkisi olmayan kullanıcı için sessizce geç */ });
        });
      },
      (err) => {
        console.error('Keşif notları alınamadı:', err);
        setError('Keşif notları yüklenemedi.');
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const loadOlder = async () => {
    setLoadingOlder(true);
    try {
      const constraints = [
        where('updatedAt', '<', cutoffRef.current),
        orderBy('updatedAt', 'desc'),
        limit(OLDER_PAGE_SIZE),
      ];
      if (olderCursor) constraints.splice(2, 0, startAfter(olderCursor));
      const snapshot = await getDocs(query(collection(db, 'kesifNotes'), ...constraints));
      const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setOlderNotes(prev => [...prev, ...docs]);
      setOlderCursor(snapshot.docs[snapshot.docs.length - 1] || olderCursor);
      setHasMoreOlder(snapshot.docs.length === OLDER_PAGE_SIZE);
    } catch (err) {
      console.error('Eski keşif notları alınamadı:', err);
      setError('Eski notlar yüklenemedi.');
    } finally {
      setLoadingOlder(false);
    }
  };

  // Aramada: yüklenmemiş eski notları başlıktaki kelimeye göre sunucudan getir
  useEffect(() => {
    const firstWord = normalize(searchTerm.trim()).split(/[^0-9a-zçğıöşüâîû]+/i).filter(Boolean)[0];
    if (!firstWord || firstWord.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const snapshot = await getDocs(query(
          collection(db, 'kesifNotes'),
          where('searchKeywords', 'array-contains', firstWord.slice(0, 20)),
          limit(SEARCH_LIMIT)
        ));
        if (!cancelled) setSearchResults(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Keşif araması yapılamadı:', err);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, SEARCH_DELAY);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const visibleNotes = useMemo(() => {
    const term = normalize(searchTerm.trim());
    // Canlı liste her zaman önceliklidir (eski bir not düzenlenince canlı listeye geçer)
    const byId = new Map();
    [...notes, ...olderNotes, ...(term ? searchResults : [])].forEach(n => {
      if (!byId.has(n.id)) byId.set(n.id, n);
    });
    return Array.from(byId.values())
      .filter(n => {
        if (onlyOwnData && n.createdBy !== user?.uid) return false;
        if (!term) return true;
        return normalize(`${n.title || ''}\n${n.content || ''}`).includes(term);
      })
      .sort((a, b) => (toDate(b.updatedAt) || 0) - (toDate(a.updatedAt) || 0));
  }, [notes, olderNotes, searchResults, searchTerm, onlyOwnData, user?.uid]);

  const groupedNotes = useMemo(() => {
    const now = new Date();
    const groups = [];
    visibleNotes.forEach(n => {
      const label = getGroupLabel(toDate(n.updatedAt), now);
      let group = groups.find(g => g.label === label);
      if (!group) {
        group = { label, items: [] };
        groups.push(group);
      }
      group.items.push(n);
    });
    return groups;
  }, [visibleNotes]);

  // Kaydetme: ilk yazıda dokümanı oluşturur, sonra günceller
  const persist = useCallback(async () => {
    // Değişiklik yoksa sunucuya yazma (gereksiz yazma ve sıralama değişimi olmasın)
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    const { title: t, content: c } = latestRef.current;
    const isEmpty = !t.trim() && !c.trim();

    try {
      if (!noteIdRef.current) {
        if (isEmpty) return;
        if (!createPromiseRef.current) {
          setSaveState('saving');
          createPromiseRef.current = addDoc(collection(db, 'kesifNotes'), {
            title: t,
            content: c,
            searchKeywords: buildSearchKeywords(t),
            createdBy: user?.uid || null,
            createdByEmail: user?.email || null,
            createdAt: new Date(),
            updatedAt: new Date(),
          }).then(ref => {
            noteIdRef.current = ref.id;
            return ref;
          });
          await createPromiseRef.current;
          // Oluşturma sırasında yazılmaya devam edildiyse son hali de kaydet
          if (dirtyRef.current) {
            await persist();
            return;
          }
          setSaveState('saved');
          return;
        }
        await createPromiseRef.current;
      }

      setSaveState('saving');
      await updateDoc(doc(db, 'kesifNotes', noteIdRef.current), {
        title: t,
        content: c,
        searchKeywords: buildSearchKeywords(t),
        updatedAt: new Date(),
      });
      setSaveState('saved');
    } catch (err) {
      console.error('Keşif notu kaydedilemedi:', err);
      dirtyRef.current = true;
      setSaveState('error');
    }
  }, [user?.uid, user?.email]);

  const removeLocalNote = (id) => {
    setOlderNotes(prev => prev.filter(n => n.id !== id));
    setSearchResults(prev => prev.filter(n => n.id !== id));
  };

  const scheduleSave = () => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      persist();
    }, AUTOSAVE_DELAY);
  };

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    latestRef.current = { ...latestRef.current, title: e.target.value };
    dirtyRef.current = true;
    scheduleSave();
  };

  const handleContentChange = (e) => {
    setContent(e.target.value);
    latestRef.current = { ...latestRef.current, content: e.target.value };
    dirtyRef.current = true;
    scheduleSave();
  };

  const openEditor = (note) => {
    noteIdRef.current = note?.id || null;
    createPromiseRef.current = null;
    latestRef.current = { title: note?.title || '', content: note?.content || '' };
    dirtyRef.current = false;
    setTitle(note?.title || '');
    setContent(note?.content || '');
    setSaveState(note ? 'saved' : 'idle');
    setShowDeleteConfirm(false);
    setEditorOpen(true);
  };

  const closeEditor = async () => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
    const { title: t, content: c } = latestRef.current;
    setEditorOpen(false);

    if (createPromiseRef.current) {
      try { await createPromiseRef.current; } catch { /* hata persist içinde gösterildi */ }
    }
    // Tamamen boşaltılan not silinir (iPhone Notlar davranışı)
    if (noteIdRef.current && !t.trim() && !c.trim()) {
      const id = noteIdRef.current;
      try {
        await deleteDoc(doc(db, 'kesifNotes', id));
        removeLocalNote(id);
      } catch (err) { console.error(err); }
      return;
    }
    await persist();
  };

  const handleDelete = async () => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
    try {
      if (createPromiseRef.current) await createPromiseRef.current;
      if (noteIdRef.current) {
        await deleteDoc(doc(db, 'kesifNotes', noteIdRef.current));
        removeLocalNote(noteIdRef.current);
      }
      noteIdRef.current = null;
      dirtyRef.current = false;
      latestRef.current = { title: '', content: '' };
      setShowDeleteConfirm(false);
      setEditorOpen(false);
    } catch (err) {
      console.error('Keşif notu silinemedi:', err);
      setSaveState('error');
      setShowDeleteConfirm(false);
    }
  };

  // Sayfadan ayrılırken bekleyen kaydı gönder
  const persistRef = useRef(persist);
  persistRef.current = persist;
  useEffect(() => () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      persistRef.current();
    }
  }, []);

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      contentRef.current?.focus();
    }
  };

  if (editorOpen) {
    return (
      <div className="kesif-page kesif-editor">
        <div className="kesif-editor-toolbar">
          <button type="button" className="kesif-round-btn" onClick={closeEditor} aria-label="Geri">
            <i className="fas fa-chevron-left"></i>
          </button>
          <span className={`kesif-save-state kesif-save-${saveState}`}>
            {saveState === 'saving' && 'Kaydediliyor...'}
            {saveState === 'saved' && 'Kaydedildi'}
            {saveState === 'error' && 'Kaydedilemedi!'}
          </span>
          <button
            type="button"
            className="kesif-round-btn kesif-round-btn-danger"
            onClick={() => setShowDeleteConfirm(true)}
            aria-label="Notu sil"
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>

        <input
          className="kesif-editor-title"
          value={title}
          onChange={handleTitleChange}
          onKeyDown={handleTitleKeyDown}
          placeholder="Müşteri adı / adres"
          autoFocus={!title}
        />
        <textarea
          ref={contentRef}
          className="kesif-editor-content"
          value={content}
          onChange={handleContentChange}
          placeholder={'Salon: 320 x 260\nYatak Odası: 180 x 240\n...'}
        />

        {showDeleteConfirm && (
          <div className="kesif-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="kesif-modal" onClick={e => e.stopPropagation()}>
              <h3>Not silinsin mi?</h3>
              <p>"{title || 'Başlıksız not'}" kalıcı olarak silinecek.</p>
              <div className="kesif-modal-actions">
                <button type="button" onClick={() => setShowDeleteConfirm(false)}>Vazgeç</button>
                <button type="button" className="kesif-modal-danger" onClick={handleDelete}>Sil</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="kesif-page">
      <h2 className="kesif-title">{pageTitle}</h2>
      <p className="kesif-subtitle">{visibleNotes.length} keşif notu</p>

      <PlanReminders
        user={user}
        onlyOwnData={onlyOwnData}
        allowedTypes={reminderTypes}
        defaultFilter={reminderDefaultFilter}
      />

      {loading && <div className="kesif-empty">Yükleniyor...</div>}
      {error && <div className="kesif-empty kesif-error">{error}</div>}

      {!loading && !error && groupedNotes.length === 0 && !searching && (
        <div className="kesif-empty">
          {searchTerm ? 'Aramanızla eşleşen not yok.' : 'Son 3 ayda keşif notu yok. Sağ alttaki butonla müşteri ekleyin.'}
        </div>
      )}

      {groupedNotes.map(group => (
        <section key={group.label} className="kesif-group">
          <h3 className="kesif-group-title">{group.label}</h3>
          <div className="kesif-card">
            {group.items.map(note => (
              <button type="button" key={note.id} className="kesif-row" onClick={() => openEditor(note)}>
                <span className="kesif-row-title">{note.title?.trim() || 'Başlıksız not'}</span>
                <span className="kesif-row-meta">
                  <span className="kesif-row-date">{formatDate(toDate(note.updatedAt))}</span>
                  <span className="kesif-row-preview">{getPreview(note.content)}</span>
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}

      {!loading && searchTerm.trim() && (
        <p className="kesif-hint">
          {searching
            ? 'Eski notlarda aranıyor...'
            : '3 aydan eski notlarda yalnızca başlıktaki kelimelere göre aranır.'}
        </p>
      )}

      {!loading && !searchTerm.trim() && (
        hasMoreOlder ? (
          <button type="button" className="kesif-load-more" onClick={loadOlder} disabled={loadingOlder}>
            {loadingOlder
              ? 'Yükleniyor...'
              : olderNotes.length ? 'Daha eski notları göster' : '3 aydan eski notları göster'}
          </button>
        ) : (
          <p className="kesif-hint">Tüm notlar yüklendi.</p>
        )
      )}

      <div className="kesif-bottom-bar">
        <div className="kesif-search">
          <i className="fas fa-search"></i>
          <input
            type="search"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Arayın"
          />
          {searchTerm && (
            <button type="button" onClick={() => setSearchTerm('')} aria-label="Aramayı temizle">
              <i className="fas fa-times-circle"></i>
            </button>
          )}
        </div>
        <button type="button" className="kesif-add-btn" onClick={() => openEditor(null)} aria-label="Müşteri Ekle" title="Müşteri Ekle">
          <i className="fas fa-pen-to-square"></i>
        </button>
      </div>
    </div>
  );
};

export default KesifList;
